import { convertTime } from '@/utils/helperTime';
import { darkenColor } from '@/utils/rgb';
import closeIcon from '@iconify/icons-eva/close-fill';
import plusIcon from '@iconify/icons-eva/plus-outline';
import { Icon } from '@iconify/react';
import { Box, Button, Divider, Stack, Typography, useTheme } from '@mui/material';
import { DateTime } from 'luxon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { EffectDateSelector } from './effectDateSelector';
import { EffectiveDateTimeType, useEffectDateTimeList, weekdayList } from './type/type';
import { RecurringWeekDays } from './weekDays';

interface EffectiveDateTImeProps {
  effectiveDateTime: EffectiveDateTimeType;
  index: number;
}

const EffectiveDateTIme: React.FC<EffectiveDateTImeProps> = (props) => {
  const { t } = useTranslation();
  const { dateTimeList, setDateTimeList, isOffer = false } = useEffectDateTimeList();
  const { effectiveDateTime, index } = props;
  const textColor = isOffer ? '#DCC179' : '#00AB55';
  const theme = useTheme();
  const btnBackgroundColor = isOffer ? '#DCC179' : theme.palette.primary.main;
  const handleAddEffDateTime = (): void => {
    const copyList = [...dateTimeList];
    const tempDate: EffectiveDateTimeType = {
      startDate: DateTime.now().toFormat('yyyy-MM-dd'),
      endDate: DateTime.now().toFormat('yyyy-MM-dd'),
      hours: [
        {
          openStart: convertTime(DateTime.now().startOf('day').toJSDate(), 'time'),
          openEnd: convertTime(DateTime.now().endOf('day').toJSDate(), 'time')
        }
      ],
      recurringWeekDays: weekdayList
    };
    copyList.push(tempDate);
    setDateTimeList(copyList);
  };

  const handleDeleteEffDateTime = (index: number): void => {
    const copyList = [...dateTimeList];
    copyList.splice(index, 1);
    setDateTimeList(copyList);
  };

  return (
    <div className="recurring-form">
      <div className="recurring-form__content">
        <Box
          sx={{
            padding: '12px 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography sx={{ color: textColor, fontWeight: 700 }}>
            {t('Effective date time')}
          </Typography>
          {dateTimeList.length > 1 && (
            <Icon
              icon={closeIcon}
              width={32}
              height={32}
              color="#00AB55"
              cursor="pointer"
              onClick={() => handleDeleteEffDateTime(index)}
            />
          )}
        </Box>
        <Divider light />
        <Stack spacing={{ xs: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }}>
            <RecurringWeekDays weekDays={effectiveDateTime.recurringWeekDays} index={index} />
          </Stack>
          <EffectDateSelector index={index} effectDate={effectiveDateTime} />

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              sx={{
                width: '100%',
                backgroundColor: btnBackgroundColor,
                '&:hover': {
                  backgroundColor: darkenColor(btnBackgroundColor, 20)
                },
                color: '#FFFFFF'
              }}
              onClick={handleAddEffDateTime}
            >
              <Icon icon={plusIcon} width={16} height={16} />
              &nbsp;
              <span>{t('Add effective date time')}</span>
            </Button>
          </Box>
        </Stack>
      </div>
    </div>
  );
};

export const useEffectiveDateTime = (props: EffectiveDateTImeProps): { DateTime: JSX.Element } => {
  const { effectiveDateTime, index } = props;

  const DateTime = <EffectiveDateTIme effectiveDateTime={effectiveDateTime} index={index} />;

  return { DateTime };
};
